/**
 * Mark one or more transactions as EXTERNAL (one-sided) transfers (ADR 0039).
 *
 * Prompts for a single free-text counterparty label applied to every selected
 * transaction, then fires one `POST /external` per transaction. Used from the
 * Transactions screen row action and bulk action bar.
 */

import { useState } from 'react';
import { FormModal } from '../FormModal';
import { useMarkExternalTransfer, type MarkExternalTransferResult } from '../../api/finance';
import { financeErrorMessage } from '../../screens/finance/finance-format';
import formStyles from '../FormModal.module.css';

export function ExternalTransferModal({
  transactionIds,
  onClose,
  onDone,
}: {
  /** Non-empty → modal is open; the transactions to mark as external. */
  transactionIds: string[] | null;
  onClose: () => void;
  /** Invoked after all transactions are marked (e.g. to clear the selection). */
  onDone?: (() => void) | undefined;
}) {
  const mark = useMarkExternalTransfer();
  const open = !!transactionIds && transactionIds.length > 0;
  const count = transactionIds?.length ?? 0;

  const [counterparty, setCounterparty] = useState('');
  const [seenKey, setSeenKey] = useState<string | null>(null);
  // Summary shown when some (but not all) legs failed — the mutation itself
  // resolves rather than rejecting on partial failure, so this doesn't come
  // from `mark.error`.
  const [partialFailure, setPartialFailure] = useState<string | null>(null);

  // Reset the field + mutation state each time a new selection opens the modal.
  const key = transactionIds ? transactionIds.join(',') : null;
  if (open && key !== seenKey) {
    setSeenKey(key);
    setCounterparty('');
    setPartialFailure(null);
    mark.reset();
  }
  if (!open && seenKey !== null) setSeenKey(null);

  const trimmed = counterparty.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 120;

  const submit = () => {
    if (!transactionIds || !valid || mark.isPending) return;
    setPartialFailure(null);
    mark.mutate(
      { transactionIds, counterparty: trimmed },
      {
        onSuccess: (result: MarkExternalTransferResult) => {
          if (result.failed === 0) {
            onDone?.();
            onClose();
            return;
          }
          // Partial failure: some legs were already marked (queries were
          // invalidated in onSettled), so a blind retry would re-submit
          // legs that already succeeded. Keep the modal open and summarize
          // instead — the user can retry with the remaining transactions.
          const firstMsg = financeErrorMessage(result.firstError) ?? 'unknown error';
          setPartialFailure(
            `Marked ${result.marked} of ${result.requested}. ${result.failed} failed: ${firstMsg}`,
          );
        },
      },
    );
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={count > 1 ? `Mark ${count} as external transfer` : 'Mark as external transfer'}
      subtitle="For legs of a real transfer whose other account isn't tracked here. They stop counting as income/expense but stay in your balances."
      submitLabel={mark.isPending ? 'Marking…' : count > 1 ? `Mark ${count}` : 'Mark as external'}
      submitDisabled={!valid || mark.isPending}
      onSubmit={submit}
      error={partialFailure ?? financeErrorMessage(mark.error)}
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="ext-counterparty">
          Counterparty
        </label>
        <input
          id="ext-counterparty"
          className={formStyles.input}
          value={counterparty}
          maxLength={120}
          placeholder="e.g. External savings, Broker, Family"
          onChange={(e) => setCounterparty(e.target.value)}
        />
        <span className={formStyles.fieldHint}>
          A label for the untracked account this money moved to or from.
        </span>
      </div>
    </FormModal>
  );
}
